<template>
  <div class="bg-white dark:bg-[#0a0e19] py-[60px] md:py-[80px] lg:py-[135px]">
    <div
      class="mx-auto px-[12.5px] md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1255px]"
    >
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-[25px] items-center">
        <div
          class="xl:ltr:-mr-[25px] xl:rtl:-ml-[25px] 2xl:ltr:-mr-[45px] 2xl:rtl:-ml-[45px] rounded-[25px] order-2 lg:order-1"
        >
          <img
            src="@/assets/images/reset-password.jpg"
            alt="reset-password-image"
            class="rounded-[25px]"
          />
        </div>
        <div
          class="xl:ltr:pl-[90px] xl:rtl:pr-[90px] 2xl:ltr:pl-[120px] 2xl:rtl:pr-[120px] order-1 lg:order-2"
        >
          <img
            src="@/assets/images/logos/sidebarlogo.png"
            alt="logo"
            class="inline-block max-w-[160px]"
          />
          <div class="my-[17px] md:my-[25px]">
            <h1
              class="font-semibold text-[22px] md:text-xl lg:text-2xl mb-[5px] md:mb-[10px]"
            >
              Reset Password?
            </h1>
            <p
              class="font-medium leading-[1.5] lg:text-md text-[#445164] dark:text-gray-400"
            >
              Enter your new password and confirm it another time in the field
              below.
            </p>
          </div>
          
          <div v-if="successMessage" class="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
            {{ successMessage }}
          </div>
          <div v-if="errorMessage" class="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {{ errorMessage }}
          </div>
          
          <form @submit.prevent="handleResetPassword">
            <div class="mb-[15px] relative" id="passwordHideShow2">
              <label
                class="mb-[10px] md:mb-[12px] text-black dark:text-white font-medium block"
              >
                New Password
              </label>
              <input
                v-model="newPassword"
                :type="showPassword1 ? 'text' : 'password'"
                class="h-[55px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500"
                id="password2"
                placeholder="Type password"
                required
                minlength="6"
              />
              <button
                @click="togglePasswordVisibility(1)"
                class="absolute text-lg ltr:right-[20px] rtl:left-[20px] bottom-[12px] transition-all hover:text-primary-500"
                type="button"
              >
                <i :class="showPassword1 ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
              </button>
            </div>
            <div class="mb-[15px] relative" id="passwordHideShow3">
              <label
                class="mb-[10px] md:mb-[12px] text-black dark:text-white font-medium block"
              >
                Confirm Password
              </label>
              <input
                v-model="confirmPassword"
                :type="showPassword2 ? 'text' : 'password'"
                class="h-[55px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500"
                id="password3"
                placeholder="Type password"
                required
                minlength="6"
              />
              <button
                @click="togglePasswordVisibility(2)"
                class="absolute text-lg ltr:right-[20px] rtl:left-[20px] bottom-[12px] transition-all hover:text-primary-500"
                type="button"
              >
                <i :class="showPassword2 ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
              </button>
            </div>
            <button
              type="submit"
              :disabled="isLoading"
              class="md:text-md block w-full text-center transition-all rounded-md font-medium mt-[20px] md:mt-[25px] py-[12px] px-[25px] text-white bg-primary-500 hover:bg-primary-400 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <span class="flex items-center justify-center gap-[5px]">
                <i v-if="!isLoading" class="material-symbols-outlined"> autorenew </i>
                <span v-if="isLoading" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                {{ isLoading ? 'Updating...' : 'Update Password' }}
              </span>
            </button>
          </form>
          <p class="mt-[15px] md:mt-[20px]">
            Back to
            <RouterLink
              to="/authentication/sign-in"
              class="text-primary-500 transition-all font-semibold hover:underline"
            >
              Sign In
            </RouterLink>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { supabase } from "@/utils/supabase";

export default defineComponent({
  name: "ResetPassword",
  components: {
    RouterLink
  },
  setup() {
    const router = useRouter();

    const newPassword = ref("");
    const confirmPassword = ref("");
    const isLoading = ref(false);
    const errorMessage = ref("");
    const successMessage = ref("");
    const showPassword1 = ref(false);
    const showPassword2 = ref(false);

    // קבלת הטוקן מה-URL
    const getAccessToken = () => {
      // נסה לקבל מהפרגמנט (אחרי ה-#)
      const hash = window.location.hash;
      if (hash && hash.includes("access_token=")) {
        const params = new URLSearchParams(hash.substring(1));
        return params.get("access_token");
      }
      
      // נסה לקבל מהפרמטרים (אחרי ה-?)
      const query = new URLSearchParams(window.location.search);
      if (query.get("token")) {
        return query.get("token");
      }
      
      return null;
    };

    // הפעלת/כיבוי הצגת הסיסמה
    const togglePasswordVisibility = (field: number) => {
      if (field === 1) {
        showPassword1.value = !showPassword1.value;
      } else if (field === 2) {
        showPassword2.value = !showPassword2.value;
      }
    };

    // טיפול בשינוי סיסמה
    const handleResetPassword = async () => {
      try {
        // בדיקה שהסיסמאות זהות
        if (newPassword.value !== confirmPassword.value) {
          errorMessage.value = "הסיסמאות אינן תואמות";
          return;
        }
        
        // בדיקת אורך מינימלי
        if (newPassword.value.length < 6) {
          errorMessage.value = "הסיסמה חייבת להיות באורך 6 תווים לפחות";
          return;
        }
        
        isLoading.value = true;
        errorMessage.value = "";
        
        // עדכון הסיסמה
        const { error } = await supabase.auth.updateUser({
          password: newPassword.value
        });
        
        if (error) {
          errorMessage.value = error.message || "אירעה שגיאה בעדכון הסיסמה";
          return;
        }
        
        successMessage.value = "הסיסמה עודכנה בהצלחה!";
        newPassword.value = "";
        confirmPassword.value = "";
        
        // הפניה לדף התחברות אחרי הצלחה
        setTimeout(() => {
          router.push("/authentication/sign-in");
        }, 2000);
        
      } catch (error: any) {
        console.error("Error resetting password:", error);
        errorMessage.value = error?.message || "אירעה שגיאה בלתי צפויה";
      } finally {
        isLoading.value = false;
      }
    };

    // בדיקה שהמשתמש הגיע עם טוקן תקף
    onMounted(async () => {
      try {
        // נסה לקבל את הטוקן מה-URL
        const accessToken = getAccessToken();
        
        // אם אין טוקן, שלח להזדהות מחדש
        if (!accessToken) {
          errorMessage.value = "קישור לא תקף. נא לבקש קישור חדש לאיפוס סיסמה.";
          setTimeout(() => {
            router.push("/authentication/forgot-password");
          }, 3000);
          return;
        }
        
        // בדוק אם הסשן תקף
        const { data, error } = await supabase.auth.getSession();
        
        if (error || !data.session) {
          console.log("User not logged in, trying to set session from URL");
        }
      } catch (error) {
        console.error("Error during component initialization:", error);
        errorMessage.value = "אירעה שגיאה. נא לנסות שוב מאוחר יותר.";
      }
    });

    return {
      newPassword,
      confirmPassword,
      isLoading,
      errorMessage,
      successMessage,
      showPassword1,
      showPassword2,
      togglePasswordVisibility,
      handleResetPassword
    };
  }
});
</script>
